import { Node, mergeAttributes } from '@tiptap/core';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';

export type MentionOptions = {
  HTMLAttributes: Record<any, any>;
  suggestion: Omit<SuggestionOptions, 'editor'>;
};

export const CommunityMention = Node.create<MentionOptions>({
  name: 'mention',

  defaultOptions: {
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: {
      char: '@',
      command: ({ editor, range, props }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: 'mention',
              attrs: props,
            },
            {
              type: 'text',
              text: ' ',
            },
          ])
          .run();
      },
      allow: ({ editor, range }) => {
        return editor.can().insertContentAt(range, { type: 'mention' });
      },
    },
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: element => {
          return {
            name: element.getAttribute('data-mention'),
          };
        },
        renderHTML: attributes => {
          if (!attributes.name) {
            return {};
          }

          const pathname = location.pathname.split('/')[1];

          return {
            'data-mention': attributes.name,
            href: `/${pathname}/community-profile/${attributes.userMentionedId}`,
          };
        },
      },
      userMentionedId: {
        default: null,
        parseHTML: element => {
          return {
            userMentionedId: element.getAttribute('userMentionedId'),
          };
        },
        renderHTML: attributes => {
          if (!attributes.userMentionedId) {
            return {};
          }

          return {
            userMentionedId: attributes.userMentionedId,
          };
        },
      },
      id: {
        default: null,
        parseHTML: element => {
          return {
            id: element.getAttribute('id'),
          };
        },
        renderHTML: attributes => {
          if (!attributes.id) {
            return {};
          }

          return {
            id: attributes.id,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-mention]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      `${this.options.suggestion.char}${node.attrs.name}`,
    ];
  },
  renderText({ node }) {
    return `${this.options.suggestion.char}${node.attrs.name}`;
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              );

              return false;
            }
          });

          return isMention;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default {
  CommunityMention,
};
